
import { useState } from "react";
import { TimeEntryForm, TimeEntry } from "@/components/TimeEntryForm";
import { TimeEntriesList } from "@/components/TimeEntriesList";
import { Project } from "@/types/Project";
import { useIsMobile } from "@/hooks/use-mobile";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

interface TimeEntriesSectionProps {
  entries: TimeEntry[];
  projects: Project[];
  isLoading: boolean;
  onAddEntry: (entry: TimeEntry) => void;
  onUpdateEntry: (id: string, entry: Partial<TimeEntry>) => void;
  onDeleteEntry: (id: string) => void;
}

export function TimeEntriesSection({ 
  entries, 
  projects, 
  isLoading, 
  onAddEntry,
  onUpdateEntry,
  onDeleteEntry 
}: TimeEntriesSectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useIsMobile();

  const filteredEntries = searchQuery 
    ? entries.filter(entry => {
        const lowerCaseQuery = searchQuery.toLowerCase();
        const project = projects.find(p => p.id === entry.projectId);
        return (
          entry.description.toLowerCase().includes(lowerCaseQuery) ||
          (project && project.name.toLowerCase().includes(lowerCaseQuery))
        );
      })
    : entries;

  return (
    <div className="space-y-4 w-full overflow-x-hidden">
      <TimeEntryForm onSubmit={onAddEntry} projects={projects} />
      
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Loading time entries...
        </div>
      ) : (
        <>
          <div className={`${isMobile ? "flex flex-col gap-2" : "flex items-center"}`}>
            <h2 className={`${isMobile ? "text-lg mb-1" : "text-xl"} font-semibold`}>Time Entries</h2>
            <div className={`${isMobile ? "w-full" : "flex-1 ml-4"} relative`}>
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search entries..."
                className={`w-full ${isMobile ? "pl-8 py-1 text-sm h-9" : "pl-8"}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <TimeEntriesList 
            entries={filteredEntries} 
            onDelete={onDeleteEntry}
            onUpdate={onUpdateEntry}
            projects={projects}
          />
        </>
      )}
    </div>
  );
}
